import { Record } from "@baseline/common";
import { Uri } from "@baseline/common/uri";
import { RoutePath, RoutePathFor, RoutePathSpecs } from "./route-path";


export type RouteMap<P extends RoutePath, M extends Record<string, RouteMapNode>> = RouteMapNode<P> & M;

export namespace RouteMap
{
    export function build<T extends RouteMapSpecs>(specs: T): RouteMapFor<T>
    {
        const routeMap = createRouteMap(specs);
        return routeMap;
    }

    function createRouteMap<T extends RouteMapSpecs>(specs: T, parent?: RouteMapNode): RouteMapFor<T>
    {
        let [routeSpecs, subRoutesSpecs] = specs;
        const hasSubRoutes = !!subRoutesSpecs;

        // Extract RoutePath specs
        const [path, argumentTypes] =
            (typeof routeSpecs == "string") ?
                [routeSpecs] :
                routeSpecs;

        // Compute RoutePath
        const fullPath = Uri.join(parent?.path.path ?? "", path);
        const routePath = RoutePath.build(fullPath, argumentTypes);

        // Create RouteMapNode
        const routeMap: RouteMapNode =
        {
            path: routePath,
            ...(parent && { parent })
        };

        // Build sub routes
        if (hasSubRoutes)
        {
            const subRoutes: Record<string, RouteMapNode> =
                Record.from(
                    Record.entries(subRoutesSpecs)
                        .map(([name, subRouteSpecs]) => [name, createRouteMap(subRouteSpecs, routeMap)])
                );

            Object.assign(routeMap, subRoutes);
        }

        // Return as RouteMap
        return routeMap as RouteMapFor<T>;
    }
}


export type RouteMapSpecs = [RoutePathSpecs, Record<string, RouteMapSpecs>?];


export type RouteMapFor<T extends RouteMapSpecs> = RouteMap<RoutePathFor<T[0]>, { [K in keyof T[1]]: RouteMapFor<T[1][K]>; }>;


interface RouteMapNode<T extends RoutePath = RoutePath>
{
    path: T;
    parent?: RouteMapNode;
}
